/**
 * Handles header hover interactions by setting up event listeners for navigation items
 * that have menus. Updates CSS variables and classes on mouseenter/mouseleave.
 */
function handleHeaderHover() {
  const headerWrapper = document.querySelector('.header-wrapper');
  const headerNavigation = headerWrapper?.querySelector('[data-nav]');
  const headerNavigationItems = headerWrapper?.querySelectorAll('[data-nav] > li');
  const defaultVariables = getVariables(headerWrapper);

  headerNavigationItems.forEach(navItem => {
    const variables = getItemVariables(navItem.dataset.handle);
    const hasMenu = navItem.dataset.hasMenu === 'true' ? true : false;

    if (hasMenu) {
      navItem.addEventListener('mouseenter', updatesVariables.bind(this, headerWrapper, variables));

      navItem.addEventListener('mouseleave', resetVariables.bind(this, headerWrapper, defaultVariables));
    }
  });
}

/**
 * Updates CSS variables and classes for header hover state
 * @param {HTMLElement} header - The header wrapper element
 * @param {Object} variables - Object containing color variables
 * @param {string} variables.featured_color - The featured color value
 * @param {string} variables.items_color - The items color value
 * @param {string} variables.appllies_color - Whether color applies to header
 */
function updatesVariables(header, variables) {
  if (variables['featured_color']) {
    header.style.setProperty('--megamenu-background', variables['featured_color']);
  }

  if (variables['items_color']) {
    header.style.setProperty('--header-hover-color', variables['items_color']);
  }

  if (variables['featured_color'] && variables['appllies_color'] === 'header') {
    header.style.setProperty('--header-hover-background', variables['featured_color']);

    header.classList.add('header-wrapper--change-background', `header-wrapper--items-color-${variables['items_color']}`);
  }

  header.classList.add('header-wrapper--hover');
}

/**
 * Resets CSS variables and removes hover classes from header
 * @param {HTMLElement} header - The header wrapper element
 * @param {Object} variables - Object containing default CSS variable values
 */
function resetVariables(header, variables) {
  for (const [key, value] of Object.entries(variables)) {
    header.style.setProperty(key, value);
  }

  header.classList.remove('header-wrapper--hover', 'header-wrapper--change-background', 'header-wrapper--items-color-white', 'header-wrapper--items-color-black');
}

/**
 * Extracts CSS custom properties (variables) from an element's inline styles
 * @param {HTMLElement} element - The element to extract variables from
 * @returns {Object} Object containing CSS variable names and their values
 */
function getVariables(element) {
  const variables = {};
  const propertiesArray = Array.from(element.style).filter((key) => key.startsWith('--'));

  propertiesArray.forEach(propertyName => {
    variables[propertyName] = element.style.getPropertyValue(propertyName);
  })

  return variables;
}

/**
 * Gets navigation item specific color variables from CSS custom properties
 * @param {string} handle - The navigation item handle used to generate CSS variable names
 * @returns {Object} Object containing featured_color, appllies_color, and items_color values
 */
function getItemVariables(handle) {
  const variables = {};
  const getCSSProp = (element, propertyName) => getComputedStyle(element).getPropertyValue(propertyName);

  variables['featured_color'] = getCSSProp(document.documentElement, `--nav-color-${handle}`);
  variables['appllies_color'] = getCSSProp(document.documentElement, `--nav-applies-color-${handle}`);
  variables['items_color'] = getCSSProp(document.documentElement, `--nav-items-color-${handle}`);

  return variables;
}

// Global header controller for theme editor integration
window.HeaderController = {
  openMenu: function(handle) {
    const headerWrapper = document.querySelector('.header-wrapper');
    const navItem = headerWrapper?.querySelector(`[data-handle="${handle}"]`);
    const headerMenu = navItem?.querySelector('header-menu');
    
    if (!headerWrapper || !navItem || !headerMenu) return false;

    // Close all other menus first
    headerWrapper.querySelectorAll('header-menu').forEach(menu => {
      if (menu !== headerMenu) {
        menu.close();
        menu.classList.remove('header-menu--theme-editor-active');
      }
    });

    // Open the target menu
    headerMenu.open();
    
    // Add a special class to simulate hover state for theme editor
    headerMenu.classList.add('header-menu--theme-editor-active');
    
    // Apply the hover styling
    const variables = getItemVariables(handle);
    updatesVariables(headerWrapper, variables);
    
    return true;
  },

  closeMenu: function(handle) {
    const headerWrapper = document.querySelector('.header-wrapper');
    const navItem = headerWrapper?.querySelector(`[data-handle="${handle}"]`);
    const headerMenu = navItem?.querySelector('header-menu');
    
    if (!headerWrapper || !navItem || !headerMenu) return false;

    // Close the menu
    headerMenu.close();
    headerMenu.classList.remove('header-menu--theme-editor-active');
    
    // Reset the styling
    const defaultVariables = getVariables(headerWrapper);
    resetVariables(headerWrapper, defaultVariables);
    
    return true;
  },

  closeAllMenus: function() {
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    headerWrapper.querySelectorAll('header-menu').forEach(menu => {
      menu.close();
      menu.classList.remove('header-menu--theme-editor-active');
    });

    const defaultVariables = getVariables(headerWrapper);
    resetVariables(headerWrapper, defaultVariables);
  }
};

document.addEventListener('DOMContentLoaded', handleHeaderHover);
